import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Progress } from 'antd'
import { useReduxState } from '@/hooks/reduxState'
import { DropdownMenu, DropdownToggle, UncontrolledDropdown } from 'reactstrap'
import { useLocalization } from '@/hooks/localization'
import { TabContent, TabNavItems, TabPane } from '@/components/tabs'
import { Link } from 'react-router-dom'
import moment from 'moment'
import { useDispatch } from '@/hooks/dispatch'
import { actionCreators as backgroundJobsActions } from '@/store/backgroundJobs'
import { getBackgroundJobResultUrl } from './backgroundJobUrl'
import DownloadExportButton from './downloadExportButton'
import GoToBackgroundJobsLink from '@/components/backgroundJobs/goToBackgroundJobsLink'
import { AdditionalDetails } from './additionalDetails'
import { ReportSubmissionGroupDetails } from './reportSubmissionGroupDetails'
import { Scrollbar } from '@/components/scrollbar'
import Icon from '@/components/Icon'
import { ErrorDetails } from './errorDetails'
import { getStore } from '@/store'
import { switchInstitution } from '@/utils/switchInstitution'
import debug from 'debug'
import { useSelector } from 'react-redux'
import { BackgroundJobMessagesButton } from '@/components/backgroundJobs/backgroundJobMessagesButton'

const log = debug('backgroundJobMenu')

function BackgroundJobMenuItem ({ collapsed, onContentClick }) {
  const backgroundJobDropdown = useRef(null)
  let jobs = useReduxState(state => limitJobs(sortJobs(state.backgroundJobs.jobs)))

  log('Background jobs %o', jobs)

  const runningJobs = jobs.filter(j => j.status === 0)
  const runningJobsCount = runningJobs.length
  log('Background running jobs %o', runningJobs)
  const isComplete = runningJobsCount === 0
  const percent = runningJobs.length ? (runningJobs.map(x => x.progress).reduce((a, b) => a + b, 0) / runningJobsCount) : 100
  log('Background job summary - Jobs: %d, Total percent: %d', runningJobsCount, percent)
  const format = useCallback(percent => runningJobsCount, [ runningJobsCount ])
  const status = jobs.find(j => j.status === 0) ? 'active' : (jobs.find(j => j.status === 2) ? 'exception' : 'success')
  const { localize } = useLocalization()

  // Animation
  const { newestJobTime } = useSelector(state => { return { newestJobTime: state.backgroundJobs.newestJobTime } })
  const [savedTime, setSavedTime] = useState(newestJobTime)
  const [animate, setAnimate] = useState(false)
  useEffect(() => {
    let handle

    if (newestJobTime !== null) {
      if (savedTime === null || newestJobTime > savedTime) {
        setAnimate(true)
        setSavedTime(newestJobTime)
        handle = setTimeout(() => {
          handle = 0
          setAnimate(false)
        },
        1800)
      }
    }

    return () => {
      if (handle) clearTimeout(handle)
    }
  }, [newestJobTime]) //eslint-disable-line

  if (jobs.length === 0) {
    return (
      <div className='dropdown--button'>
        <GoToBackgroundJobsLink className='nav-link btn-bgjobs-free' title='Common.BackgroundJobsHistory'
          showIcon />
      </div>
    )
  }

  function sortJobs (jobs) {
    jobs.sort(function (a, b) {
      try {
        if (a.status < b.status) {
          return -1
        } else if (a.status > b.status) {
          return 1
        }

        const endDatesComparison = compareDates(a, b, 'endTime')
        if (endDatesComparison !== 0) { return endDatesComparison }

        return compareDates(a, b, 'creationTime')
      } catch (e) {
        console.error('Error sorting jobs', e)
      }
      return 0
    })
    return jobs
  }

  function compareDates (dataObjectA, dataObjectB, propertyName) {
    try {
      if ((dataObjectA[ propertyName ] || '') === '' || (dataObjectB[ propertyName ] || '') === '') { return 0 }

      const dateA = new Date(dataObjectA[ propertyName ])
      const dateB = new Date(dataObjectB[ propertyName ])

      if (dateA > dateB) {
        return -1
      } else if (dateA < dateB) {
        return 1
      }
    } catch (e) {
      console.error('Error comparing dates', e)
    }

    return 0
  }

  function limitJobs (jobs) {
    if (!jobs) {
      return []
    }
    if (jobs.length > 5) {
      jobs = jobs.slice(0, 5)
    }
    return jobs
  }

  return (
    <UncontrolledDropdown ref={backgroundJobDropdown} className='dropdown--button dropdown--bgjobs'
      direction='right'>
      <DropdownToggle nav>
        { !collapsed && <small className='bgjobs-title'>{ localize('Common.JobProgress') }</small> }
        <Progress
          percent={percent}
          status={status}
          format={!isComplete ? format : null}
          type={collapsed ? 'circle' : 'line'}
          className='ant-progress--menu-item'
          strokeLinecap='square'
          size='small'
          width={16}
          strokeWidth={collapsed ? 10 : 3}
          strokeColor='#177bca'
        />
        <span className='animation-wrapper'><span className={animate ? 'ant-progress-text--animate' : ''} /></span>
      </DropdownToggle>
      <DropdownMenu className='dropdown-menu--last'>
        <div className={'posr'}>
          <GoToBackgroundJobsLink className='btn btn-sm btn-secondary btn-bgjobs'
            title='Common.BackgroundJobsHistory' />
          <TabNavItems activeTabId='bgjobs' items={[
            {
              id: 'bgjobs',
              text: 'Common.JobProgress'
            }
          ]} />
          <TabContent activeTab='bgjobs'>
            <TabPane className='background-jobs-nav-holder' tabId='bgjobs'>
              <Scrollbar heightRelativeToParent='100%'>
                { jobs.map(j => <BackgroundJobRow key={j.id} {...j} />) }
              </Scrollbar>
            </TabPane>
          </TabContent>
        </div>
      </DropdownMenu>
    </UncontrolledDropdown>
  )
}

function BackgroundJobRow ({
  id,
  jobType,
  status,
  progress,
  message,
  data,
  creationTime,
  endTime,
  currentTime,
  onContentClick,
  showAllInfo,
  isRelatedJobs,
  backgroundJobMessages
}) {
  getStore()

  const { localize } = useLocalization()
  const [ timeCorrection, setTimeCorrection ] = useState(0)
  const [ duration, setDuration ] = useState(null)
  const [ close ] = useDispatch([ backgroundJobsActions.closeJob ])
  const onClick = useCallback(async () => {
    if (!showAllInfo) {
      if (typeof close === 'function') {
        close(id)
      }
      if (typeof onContentClick === 'function') {
        onContentClick()
      }
    }
  }, [ close, id, onContentClick, showAllInfo ])
  useEffect(() => {
    const serverNow = moment(currentTime)
    const clientNow = moment.utc()
    setTimeCorrection(serverNow.diff(clientNow, 's'))
  }, [currentTime])
  useEffect(() => {
    function calculateDuration () {
      const end = endTime ? moment(endTime) : moment.utc().add(timeCorrection, 's')
      setDuration(end.diff(moment(creationTime)))
    }

    const handle = !endTime ? setInterval(calculateDuration, 1000) : 0
    calculateDuration()
    return () => {
      if (handle) clearInterval(handle)
    }
  }, [ creationTime, timeCorrection, endTime ])

  const url = getBackgroundJobResultUrl(status, data)

  const onClickEvent = (e) => {
    switchInstitution(e, (data || {}).institutionId, url)
  }

  const progressBar = <React.Fragment>
    <Progress strokeLinecap='square' size='small' strokeWidth={3} percent={progress}
      status={getStatus(status)} />
    <div className='pull-right  mx-2 my-3'>
      { url && !isRelatedJobs &&
      <Link to={url} onClick={onClickEvent} className={'btn btn-sm btn-secondary'}><Icon
        name={'search'} /><span>{ localize('Common.Detail') }</span></Link> }
      <DownloadExportButton data={data} />
      <BackgroundJobMessagesButton backgroundJobMessages={backgroundJobMessages} />
    </div>
    <strong>{ localize(message) }</strong>
    <div className='progress-row__detail'>
      <AdditionalDetails creationTime={creationTime} data={data} />
      <small className='job-duration'><Icon name='timer' /> { getDuration(duration) }</small>
      { showAllInfo && <ReportSubmissionGroupDetails data={data} /> }
      { showAllInfo && <ErrorDetails data={data} localize={localize} /> }
    </div>

  </React.Fragment>

  return (
    <div className='progress-row' onClick={onClick}>
      { progressBar }
    </div>
  )
}

function getStatus (status) {
  switch (status) {
    case 0:
      return 'active'
    case 1:
    case 3:
    case 4:
    case 5:
      return 'success'
    default:
      return 'exception'
  }
}

function getDuration (duration) {
  if (!duration) return null
  const d = moment.duration(duration)
  const seconds = Math.round(d.asSeconds())
  if (seconds > 59) {
    return d.humanize()
  } else if (seconds < 0) {
    return '...'
  } else {
    return seconds + 's'
  }
}

export {
  BackgroundJobMenuItem,
  BackgroundJobRow
}



// WEBPACK FOOTER //
// ./src/components/backgroundJobs/backgroundJobMenuItem.js